import { useState } from 'react';
import { MoreVertical, Calendar, Clock, Archive, Trash2, Sun } from 'lucide-react';
import InlineDatePicker from './InlineDatePicker';
import InlineWaitingForm from './InlineWaitingForm';
import { isDelegated } from '../../hooks/useTasks';
import { getTodayString } from '../../utils/dateUtils';

/**
 * Overflow menu for processed views (Today, Waiting, Scheduled)
 * Shows "..." button that expands to context-appropriate pivot actions
 * Menu items are configurable based on current view:
 * - Today view:     Schedule, Waiting, Someday
 * - Waiting view:   Today, Schedule, Someday
 * - Scheduled view: Today, Waiting, Someday
 */
export default function OverflowMenu({
  task,
  onSchedule,
  onMoveToWaiting,
  onMoveToSomeday,
  onAddToToday,
  onDelete,
  disabled = false,
  showDelete = false,
  showToday = false,      // Show "Today" option (for Waiting/Scheduled views)
  hideWaiting = false,    // Hide "Waiting" option (for Waiting view)
  hideSchedule = false,   // Hide "Schedule" option (for Scheduled view)
  delegates = []
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showWaitingForm, setShowWaitingForm] = useState(false);

  const isWaiting = !!(task?.waitingFor || isDelegated(task));
  const isSomeday = task?.isSomeday;

  const handleSchedule = (date) => {
    if (date && onSchedule) {
      onSchedule(task.id, date);
      setShowDatePicker(false);
      setShowMenu(false);
    }
  };

  const handleWaiting = (waitingFor, delegate, followUpDate) => {
    if (onMoveToWaiting) {
      onMoveToWaiting(task.id, waitingFor, delegate, followUpDate);
      setShowWaitingForm(false);
      setShowMenu(false);
    }
  };

  const handleSomeday = () => {
    if (onMoveToSomeday) {
      onMoveToSomeday(task.id);
      setShowMenu(false);
    }
  };

  const handleToday = () => {
    if (onAddToToday) {
      onAddToToday(task.id);
      setShowMenu(false);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(task.id);
      setShowMenu(false);
    }
  };

  // Show inline date picker
  if (showDatePicker) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <InlineDatePicker
          onConfirm={handleSchedule}
          onCancel={() => {
            setShowDatePicker(false);
            setShowMenu(false);
          }}
        />
      </div>
    );
  }

  // Show inline waiting form
  if (showWaitingForm) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <InlineWaitingForm
          onConfirm={handleWaiting}
          onCancel={() => {
            setShowWaitingForm(false);
            setShowMenu(false);
          }}
          delegates={delegates}
        />
      </div>
    );
  }

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      {/* Trigger button */}
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={disabled}
        className={`
          p-1.5 rounded-md bg-transparent border-none cursor-pointer
          text-text-muted hover:text-text-secondary hover:bg-surface-hover
          transition-all duration-200
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <MoreVertical size={16} />
      </button>

      {/* Dropdown menu */}
      {showMenu && (
        <>
          {/* Backdrop to close menu */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowMenu(false)}
          />

          {/* Menu */}
          <div className="absolute right-0 top-full mt-1 bg-surface-card border border-border rounded-lg shadow-lg z-20 py-1 min-w-[140px]">
            {/* Today - shown for Waiting/Scheduled views */}
            {showToday && onAddToToday && (
              <button
                onClick={handleToday}
                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-surface-hover transition-colors text-text-primary"
              >
                <Sun size={14} />
                Today
              </button>
            )}

            {/* Schedule - hidden in Scheduled view */}
            {!hideSchedule && (
              <button
                onClick={() => setShowDatePicker(true)}
                className={`
                  w-full px-3 py-2 text-left text-sm flex items-center gap-2
                  hover:bg-surface-hover transition-colors
                  ${task?.scheduledDate ? 'text-weekly' : 'text-text-primary'}
                `}
              >
                <Calendar size={14} />
                Schedule
              </button>
            )}

            {/* Waiting - hidden in Waiting view */}
            {!hideWaiting && (
              <button
                onClick={() => setShowWaitingForm(true)}
                className={`
                  w-full px-3 py-2 text-left text-sm flex items-center gap-2
                  hover:bg-surface-hover transition-colors
                  ${isWaiting ? 'text-amber-400' : 'text-text-primary'}
                `}
              >
                <Clock size={14} />
                Waiting
              </button>
            )}

            {/* Someday */}
            <button
              onClick={handleSomeday}
              className={`
                w-full px-3 py-2 text-left text-sm flex items-center gap-2
                hover:bg-surface-hover transition-colors
                ${isSomeday ? 'text-purple-400' : 'text-text-primary'}
              `}
            >
              <Archive size={14} />
              Someday
            </button>

            {/* Delete (admin only) */}
            {showDelete && onDelete && (
              <>
                <div className="h-px bg-border my-1" />
                <button
                  onClick={handleDelete}
                  className="w-full px-3 py-2 text-left text-sm text-red-500 hover:bg-red-500/10 flex items-center gap-2 transition-colors"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
